import React from "react";
import { Modal, Form, InputNumber, Select, Input, Typography, Tag } from "antd";
import { WalletOutlined, LockOutlined } from "@ant-design/icons";

const { Option } = Select;
const { Text } = Typography;

export default function AdjustWalletModal({ user, onSubmit, onClose }) {
  const [form] = Form.useForm();

  const wallets = user?.wallets || [];

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Find selected wallet to pass full details if needed
      const selectedWallet = wallets.find((w) => w.id === values.walletId);

      onSubmit({
        userId: user.id,
        id: values.walletId,
        amount: parseFloat(values.amount),
        operation: values.operation,
        remarks: values.remarks || "",
      });
    } catch (error) {
      // Validation failed
    }
  };

  // When wallet selection changes, update displayed balance
  const selectedWalletId = Form.useWatch("walletId", form);
  const selectedWallet = wallets.find((w) => w.id === selectedWalletId);

  return (
    <Modal
      title={`Adjust Wallet — ${user?.name || ""}`}
      open={true}
      onCancel={onClose}
      onOk={handleOk}
      okText="Submit"
      centered
      destroyOnClose
    >
      {/* User Info */}
      <div
        style={{
          marginBottom: 16,
          padding: "10px 14px",
          background: "#fafafa",
          borderRadius: 8,
          border: "1px solid #f0f0f0",
        }}
      >
        <div style={{ marginBottom: 4 }}>
          <Text strong>User: </Text>
          {user?.name} ({user?.email})
        </div>
        <div>
          <Text strong>Current Balance: </Text>
          {selectedWallet
            ? `$${Number(selectedWallet.totalBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
            : wallets.length > 0
            ? "— select a wallet"
            : `$${Number(user?.wallet?.totalBalance || 0).toFixed(2)}`}
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{ operation: "CREDIT" }}
      >
        {/* Wallet Selector */}
        <Form.Item
          label="Select Wallet"
          name="walletId"
          rules={[{ required: true, message: "Please select a wallet" }]}
        >
          <Select
            placeholder="Choose wallet to adjust"
            optionLabelProp="label"
          >
            {wallets.map((wallet) => (
              <Option
                key={wallet.id}
                value={wallet.id}
                label={`Wallet #${wallet.id}${wallet.primaryWallet ? " (Primary)" : ""}`}
                disabled={!wallet.walletActive}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {wallet.walletActive ? (
                      <WalletOutlined style={{ color: "#52c41a" }} />
                    ) : (
                      <LockOutlined style={{ color: "#ff4d4f" }} />
                    )}
                    <span style={{ fontWeight: 500 }}>
                      {wallet.walletName || `Wallet #${wallet.id}`}
                    </span>
                    {wallet.primaryWallet && (
                      <Tag color="blue" style={{ borderRadius: 6, fontSize: 11, marginLeft: 4 }}>
                        Primary
                      </Tag>
                    )}
                  </span>
                  <span style={{ color: "#888", fontSize: 13 }}>
                    ${Number(wallet.totalBalance || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                    {!wallet.walletActive && (
                      <Tag
                        color="red"
                        style={{ borderRadius: 6, fontSize: 11, marginLeft: 6 }}
                      >
                        Frozen
                      </Tag>
                    )}
                  </span>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Operation */}
        <Form.Item
          label="Operation Type"
          name="operation"
          rules={[{ required: true, message: "Please select an operation" }]}
        >
          <Select>
            <Option value="CREDIT">Top Up</Option>
            <Option value="DEBIT">Deduct</Option>
            <Option value="REFUND">Refund</Option>
          </Select>
        </Form.Item>

        {/* Amount */}
        <Form.Item
          label="Amount"
          name="amount"
          rules={[
            { required: true, message: "Please enter an amount" },
            {
              validator: (_, value) =>
                value > 0
                  ? Promise.resolve()
                  : Promise.reject("Amount must be greater than zero"),
            },
          ]}
        >
          <InputNumber
            min={0.01}
            step={0.01}
            style={{ width: "100%" }}
            prefix="$"
          />
        </Form.Item>

        {/* Remarks */}
        <Form.Item
          label="Remarks"
          name="remarks"
          rules={[{ max: 200, message: "Remarks too long" }]}
        >
          <Input.TextArea
            placeholder="Reason for adjustment (optional)"
            rows={3}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}